import React, { useState, useRef, useEffect } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import LotMapInline from '../components/LotMapInline';
import HeroQuickFacts from '../components/HeroQuickFacts';
import HeroPromoBanner from '../components/HeroPromoBanner';
import TrustBand from '../components/TrustBand';
import MortgageCalculator from '../components/MortgageCalculator';
import Testimonials from '../components/Testimonials';
import VirtualTour from '../components/VirtualTour';
import LocationMapPro from '../components/LocationMapPro';
import SunsetGallery from '../components/SunsetGallery';
import HechoAMano, { homeCraftPool } from '../components/HechoAMano';
import FAQSection from '../components/FAQSection';
import useScrollReveal from '../hooks/useScrollReveal';
import { Link } from 'react-router-dom';

const gallerySlides = [
  { bg: '/images/real/sm-mar-roof-01-lg.webp' },
  { bg: '/images/real/sm-fer-twilight-01-lg.webp' },
  { bg: '/images/real/sm-mar-cocina-01-lg.webp' },
  { bg: '/images/real/sm-mar-escalera-01-lg.webp' },
  { bg: '/images/real/sm-fer-interior-01-lg.webp' },
  { bg: '/images/real/sm-conjunto-01-lg.webp' },
];

const amenidades = [
  { icon: '/images/b-icon-c-accesos.svg', label: 'Acceso automatizado', desc: 'Lectura de tag electrónico' },
  { icon: '/images/b-icon-c-accesos.svg', label: 'Puertas cerradas 24 h', desc: 'Perímetro siempre asegurado' },
  { icon: '/images/b-icon-seguridad.svg', label: 'Vigilancia 24/7', desc: 'Personal capacitado' },
  { icon: '/images/b-icon-cctv.svg', label: 'Circuito cerrado', desc: 'Monitoreo con cámaras' },
  { icon: '/images/b-icon-mascotas.svg', label: 'Área de mascotas', desc: 'Desarrollo pet-friendly' },
  { icon: '/images/b-icon-pista.svg', label: 'Canchas de usos múltiples', desc: 'Deporte y recreación' },
  { icon: '/images/b-icon-juegos.svg', label: 'Juegos infantiles', desc: 'Área recreativa familiar' },
  { icon: '/images/icon-vegetacion.svg', label: 'Áreas verdes', desc: 'Vegetación endémica de bajo mantenimiento' },
];

const sustentableFeatures = [
  { icon: '/images/icon-vegetacion.svg', label: 'Vegetación endémica' },
  { icon: '/images/s.icon-subterraneo.svg', label: 'Instalaciones subterráneas ocultas' },
  { icon: '/images/s-icon-concreto.svg', label: 'Vialidades de concreto estampado' },
  { icon: '/images/s-icon-luminaria.svg', label: 'Alumbrado público LED' },
  { icon: '/images/icon-aire.svg', label: 'Calidad del aire' },
  { icon: '/images/icon-iluminacion.svg', label: 'Iluminación y vistas' },
];

// Cintillo del hero — vegetación + materiales + detalles arquitectónicos.
// Alternancia natural V/M/A para que la mirada respire textura/color.
const hsgOneImages = [
  { src: '/images/details/detail-13.webp' },           // agapanto (flor morada)             — V
  { src: '/images/details/detail-21-tejas.webp' },     // tejas de barro apiladas            — A
  { src: '/images/details/detail-16.webp' },           // piedra laja (gris oscura plana)    — M
  { src: '/images/details/detail-10.webp' },           // lavanda con abeja (flor morada)    — V
  { src: '/images/details/detail-22-escalera.webp' },  // escalera con azulejos talavera     — A
  { src: '/images/details/detail-19.webp' },           // boulders (piedra orgánica beige)   — M
  { src: '/images/details/detail-17.webp' },           // verdolaga (cubresuelos verde)      — V
  { src: '/images/details/detail-24-cantera.webp' },   // cantera enmarcando ventanal        — A
  { src: '/images/details/detail-18.webp' },           // madera clara (paneles verticales)  — M
  { src: '/images/details/detail-23-herreria.webp' },  // herrería de acero en balcón        — A
  { src: '/images/details/detail-20.webp' },           // gravilla blanca (chips de mármol)  — M
];
const hsgTwoImages = [
  '/images/real/sm-mar-twilight-01-lg.webp',
  '/images/real/sm-fer-cocina-01-lg.webp',
  '/images/real/sm-mar-roof-01-lg.webp',
  '/images/real/sm-conjunto-03-lg.webp',
  '/images/real/sm-mar-recamara-01-lg.webp',
  '/images/real/sm-fer-twilight-02-lg.webp',
  '/images/real/sm-mar-bano-01-lg.webp',
  '/images/real/sm-amenidad-paisaje-01-lg.webp',
];

function CtaSectionHome() {
  return null;
}

export default function HomePage() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [videoPaused, setVideoPaused] = useState(false);
  const videoRef = useRef(null);
  const totalSlides = gallerySlides.length;
  useScrollReveal();

  const prevSlide = () => setCurrentSlide(p => (p - 1 + totalSlides) % totalSlides);
  const nextSlide = () => setCurrentSlide(p => (p + 1) % totalSlides);

  useEffect(() => {
    const timer = setInterval(() => setCurrentSlide(p => (p + 1) % totalSlides), 4000);
    return () => clearInterval(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync icon state with actual video events
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onPlay  = () => setVideoPaused(false);
    const onPause = () => setVideoPaused(true);
    v.addEventListener('play', onPlay);
    v.addEventListener('pause', onPause);
    return () => {
      v.removeEventListener('play', onPlay);
      v.removeEventListener('pause', onPause);
    };
  }, []);

  const toggleVideo = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) { v.play(); } else { v.pause(); }
  };

  return (
    <div className="page-wrapper">
      <Navbar />
      <main className="main-wrapper">
        {/* HERO */}
        <section className="hero">
          <div className="hero-video-bg">
            <video ref={videoRef} autoPlay loop muted playsInline
              poster="/images/santa-monica-residencial-1.webp"
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            >
              <source src="https://uploads-ssl.webflow.com/63e41257dd77c371592bcd6e/63e477119689f70d1b06ca29_vbdSantaMonica-transcode-transcode.mp4" type="video/mp4" />
            </video>
          </div>
          <div className="hero-shade" />
          <div className="page-padding">
            <div className="container-medium">
              <div className="hero-content-wrap">
                <div className="hero-content">
                  <div className="element-content align-center">
                    <div className="element-content align-center">
                      <div className="h1-label">SANTA MÓNICA RESIDENCIAL · PACHUCA</div>
                      <h1 className="sm-h1 reveal" data-reveal>Ven a conocer tu nuevo hogar en <span className="serif-accent">Pachuca</span></h1>
                      <p className="text-size-medium reveal hero-subline" data-reveal data-reveal-delay="1">Viviendas de 3 a 4 recámaras <strong>desde $2,475,000 MXN</strong></p>
                      <HeroQuickFacts />
                      <HeroPromoBanner />
                      <div className="hero-cta-row hero-cta-single">
                        <a href="#modelos" className="btn cta-special contrast-variant big"
                          style={{ display: 'inline-flex' }}
                          onClick={e => { e.preventDefault(); document.querySelector('#modelos')?.scrollIntoView({ behavior: 'smooth' }); }}
                          data-testid="hero-cta-modelos"
                        >
                          <div className="btn-cta-bg" />
                          <div className="icon-btn-container">
                            <div className="icon-btn-text">Conoce los modelos</div>
                            <div className="icon-btn-icon"><span className="icon-font">south</span></div>
                          </div>
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Horizontal Scroll Galleries — solo el cintillo inferior (divide hero / proyecto) */}
          <div className="horizontal-scroll-galleries">
            <div className="hsg-strip hsg-one">
              {[...hsgOneImages, ...hsgOneImages, ...hsgOneImages, ...hsgOneImages].map((img, i) => (
                <div
                  key={`${img.src}-${i}`}
                  className="hsg-frame"
                  style={{
                    backgroundImage: `url(${img.src})`,
                    backgroundPosition: img.position || 'center',
                  }}
                />
              ))}
            </div>
          </div>
          <div className="hero-to-project" />
        </section>

        {/* TRUST BAND + QUICK FACTS */}
        <TrustBand />
        {/* HeroQuickFacts moved INTO the hero block above */}

        {/* PROYECTO */}
        <section id="proyecto" className="proyecto">
          <div className="page-padding">
            <div className="container-large">
              <div className="padding-vertical padding-proyecto">
                <div className="padding-bottom-large">
                  <div className="_2-grid-col stretch">
                    <div className="v-align-c-card">
                      <div className="proyecto-block">
                        <div className="proyecto-text">
                          <div className="text-label" style={{ color: 'rgba(247,252,255,0.6)' }}>Proyecto</div>
                          <h2 className="sm-h2" data-reveal>Santa Mónica Residencial es <span className="serif-accent">calidad de vida</span> para tu familia</h2>
                          <p className="proyecto-paragraph">Desarrollamos amplios espacios funcionales que promueven el bienestar y la convivencia, utilizando las mejores técnicas y materiales para la construcción de viviendas saludables.</p>
                        </div>
                        <div className="proyecto-action">
                          <button
                            className="btn-pill-ghost"
                            onClick={() => setVideoModalOpen(true)}
                            data-testid="proyecto-video-btn"
                          >
                            Video promocional
                            <span className="icon-font">play_circle</span>
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="overflow-image-frame" />
                  </div>
                </div>
                <div className="_2-grid-col stretch">
                  <div className="overflow-image-frame side-b" />
                  <div className="v-align-c-card">
                    <div className="proyecto-block">
                      <div className="proyecto-text">
                        <div className="text-label" style={{ color: 'rgba(247,252,255,0.6)' }}>Diseño arquitectónico</div>
                        <h2 className="sm-h2" data-reveal>Tu estilo,<br /><span className="serif-accent">nuestro estilo.</span></h2>
                        <p className="proyecto-paragraph">Santa Mónica Residencial ofrece un estilo mexicano contemporáneo caracterizado por el diseño arquitectónico, la selección de materiales y la vegetación endémica en un entorno urbano pensado para los clientes más exigentes.</p>
                      </div>
                      <div className="proyecto-action">
                        <Link
                          to="/modelo-fernanda"
                          className="btn-pill-ghost"
                          data-testid="proyecto-modelos-link"
                        >
                          Ver modelos
                          <span className="icon-font">east</span>
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* MODELOS */}
        <section id="modelos" className="modelos">
          <div className="page-padding">
            <div className="container-large">
              <div className="modelos-head" data-reveal>
                <div className="modelos-head-text">
                  <div className="text-label" style={{ color: 'rgba(247,252,255,0.6)' }}>nuestros modelos</div>
                  <h2 className="sm-h2">Encuentra el hogar que <span className="serif-accent">se ajusta a tu familia.</span></h2>
                  <p className="modelos-intro">
                    Dos modelos de residencia con el mismo lenguaje arquitectónico y diferentes
                    distribuciones; ambos con 3 a 4 recámaras y roof garden privado con vistas
                    abiertas al fraccionamiento. <strong>Fernanda</strong> aprovecha cada metro con
                    espacios funcionales; <strong>Mariana</strong> suma más metros de construcción
                    y mayor área social.
                  </p>
                </div>
              </div>
              <div className="padding-vertical" style={{ paddingTop: '0' }}>
                <div className="_2-grid-col">
                  <Link to="/modelo-fernanda" className="modelo-card-link modelo-b">
                    <div className="modelo-card-link_leyend">
                      <div>MODELO</div>
                      <h3 className="sm-h3">Fernanda</h3>
                      <div className="modelo-card-specs">
                        <span><span className="icon-font">bed</span>3 a 4 rec</span>
                        <span><span className="icon-font">shower</span>2.5 baños</span>
                        <span><span className="icon-font">square_foot</span>143 m²</span>
                      </div>
                    </div>
                    <img loading="lazy" src="/images/real/sm-fer-hero-card-lg.webp" alt="Modelo Fernanda" draggable={false} style={{ display: 'block', width: '100%' }} />
                  </Link>
                  <Link to="/modelo-mariana" className="modelo-card-link">
                    <img loading="lazy" src="/images/real/sm-mar-hero-card-lg.webp" alt="Modelo Mariana" draggable={false} style={{ display: 'block', width: '100%' }} />
                    <div className="modelo-card-link_leyend">
                      <div>MODELO</div>
                      <h3 className="sm-h3">Mariana</h3>
                      <div className="modelo-card-specs">
                        <span><span className="icon-font">bed</span>3 a 4 rec</span>
                        <span><span className="icon-font">shower</span>3 + 2 medios</span>
                        <span><span className="icon-font">square_foot</span>163 m²</span>
                      </div>
                    </div>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* MASTERPLAN INTERACTIVO (promovido — el asset estrella va arriba) */}
        <LotMapInline />

        {/* AMENIDADES + SUSTENTABLE (integrados, compactos y visuales) */}
        <section id="amenidades" className="amenidades">
          <div className="pleca-corte" />
          <div className="page-padding">
            <div className="container-large">
              <div className="padding-vertical padding-large" style={{ paddingTop: '4rem', paddingBottom: '2rem' }}>
                <div className="amenidades-layout">
                  {/* IZQUIERDA: foto editorial */}
                  <div className="amenidades-visual" data-testid="amenidades-visual">
                    <div className="amenidades-photo" style={{ backgroundImage: "url('/images/real/sm-entrada-03-lg.webp')" }} />
                    <div className="amenidades-photo-tag">
                      <span className="icon-font">verified</span>
                      <span>Acceso controlado con caseta de vigilancia</span>
                    </div>
                  </div>
                  {/* DERECHA: header + lista densa */}
                  <div className="amenidades-content">
                    <div className="text-label" style={{ color: 'var(--brand-accent)' }}>amenidades</div>
                    <h2 className="sm-h2" data-reveal>Complementos de tu espacio <span className="serif-accent">hacen tu hogar.</span></h2>
                    <p className="amenidades-intro">
                      <strong>Santa Mónica Residencial</strong> cuenta con la combinación perfecta de
                      seguridad, áreas comunes y diseño consciente para que vivir aquí sea la decisión correcta.
                    </p>
                    <ul className="amenidades-list">
                      {amenidades.map((item, i) => (
                        <li key={item.label} className="amenidades-list-item" data-testid={`amenidad-${i}`}>
                          <span className="amenidades-list-icon">
                            <img src={item.icon} alt={item.label} loading="lazy" />
                          </span>
                          <div className="amenidades-list-text">
                            <div className="amenidades-list-label">{item.label}</div>
                            <div className="amenidades-list-desc">{item.desc}</div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>

              {/* SUSTENTABLE — integrado como sub-bloque compacto */}
              <div className="sustentable-stripe">
                <div className="sustentable-stripe-head">
                  <div className="text-label" style={{ color: 'rgba(255,255,255,0.55)' }}>arquitectura sustentable</div>
                  <h3 className="sm-h3" data-reveal>Construidas para <span className="serif-accent">durar toda la vida</span></h3>
                </div>
                <div className="sustentable-stripe-grid">
                  {sustentableFeatures.map((f) => (
                    <div key={f.label} className="sustentable-stripe-item">
                      <img loading="lazy" src={f.icon} alt={f.label} />
                      <span>{f.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Mini divisor: madera + flores (transición Amenidades → Ubicación) */}
        {/* (removido por preferencia del usuario) */}

        {/* UBICACIÓN ESTRATÉGICA (promovida — explorer pattern) */}
        <LocationMapPro />

        {/* TOUR VIRTUAL + COMPARADOR fusionados */}
        <VirtualTour />

        {/* CALCULADORA hipoteca — temporalmente oculta */}
        {/* <MortgageCalculator /> */}

        {/* ATARDECERES en SM (roof garden) — temporalmente oculto */}
        {/* <SunsetGallery /> */}

        {/* HECHO A MANO (detalles autorales) */}
        <HechoAMano
          pool={homeCraftPool}
          displayCount={7}
          title={<>Detalles que <span className="serif-accent">no se hacen en serie.</span></>}
          description="Cada metro de Santa Mónica Residencial esconde piezas pensadas para esta comunidad: señalética propia, mobiliario diseñado a propósito, materia prima local y gestos de bienvenida. No es producción en línea: es identidad."
        />

        {/* TESTIMONIALS — temporalmente oculto */}
        {/* <Testimonials /> */}

        {/* FAQ */}
        <FAQSection />

        {/* CTA */}

        {/* CREDITOS */}
        <section className="creditos">
          <div className="page-padding">
            <div className="container-medium">
              <div className="padding-vertical padding-medium">
                <div className="_2-grid-col v-align-center">
                  <div className="element-content">
                    <div className="text-content">
                      <div className="text-label" style={{ color: 'rgba(255,255,255,0.7)' }}>créditos hipotecarios</div>
                      <h2 className="sm-h2">Pregunta por tu crédito inmobiliario</h2>
                      <p>Te ayudamos a calificar con cualquier banco o instituto público. Tu asesor compara opciones y elige la mejor tasa para tu perfil.</p>
                    </div>
                  </div>
                  <div className="element-content">
                    <img loading="lazy" src="/images/logos-creditos-1.png" alt="Créditos" style={{ width: '100%' }} />
                    <img loading="lazy" src="/images/logos-creditos-2.png" alt="Créditos" style={{ width: '100%' }} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />

      {/* VIDEO MODAL */}
      {videoModalOpen && (
        <div className="video-modal" onClick={() => setVideoModalOpen(false)}>
          <div className="video-modal-inner" onClick={e => e.stopPropagation()}>
            <button className="video-modal-close" onClick={() => setVideoModalOpen(false)}>
              <span className="icon-font">close</span>
            </button>
            <iframe
              src="https://www.youtube.com/embed/tchi5GA7P1U?autoplay=1"
              allow="autoplay; fullscreen"
              allowFullScreen
              title="Video promocional"
            />
          </div>
        </div>
      )}

      {/* LOT MAP is now inline as <LotMapInline /> above */}
    </div>
  );
}
